import { useMemo, useState, type CSSProperties } from 'react';
import { AlertCircle, AlertTriangle, CheckCircle2, Info, X } from 'lucide-react';
import { useStore } from '../store';
import { runRuleCheck, type RuleSeverity } from '../ruleCheck';

const SEVERITY_META: Record<
  RuleSeverity,
  { label: string; color: string; Icon: typeof AlertCircle }
> = {
  error: { label: 'Errors', color: 'var(--danger)', Icon: AlertCircle },
  warning: { label: 'Warnings', color: 'var(--accent)', Icon: AlertTriangle },
  info: { label: 'Info', color: 'var(--selection)', Icon: Info },
};

export function RuleCheckerModal({ onClose }: { onClose: () => void }) {
  const sheets = useStore((s) => s.sheets);
  const goToEdge = useStore((s) => s.goToEdge);
  const setActiveSheet = useStore((s) => s.setActiveSheet);
  const selectNode = useStore((s) => s.selectNode);

  const issues = useMemo(() => runRuleCheck(sheets), [sheets]);
  const counts = useMemo(
    () => ({
      error: issues.filter((i) => i.severity === 'error').length,
      warning: issues.filter((i) => i.severity === 'warning').length,
      info: issues.filter((i) => i.severity === 'info').length,
    }),
    [issues],
  );

  const [hidden, setHidden] = useState<Set<RuleSeverity>>(new Set());
  const toggle = (s: RuleSeverity) =>
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s);
      else next.add(s);
      return next;
    });

  const visible = issues.filter((i) => !hidden.has(i.severity));

  const navigate = (i: (typeof issues)[number]) => {
    if (i.edgeId) {
      goToEdge(i.edgeId);
      // goToEdge switches to the owner sheet; wait for the canvas to remount,
      // then centre the viewport on the wire's endpoints.
      window.setTimeout(
        () => window.dispatchEvent(new CustomEvent('skema:focus', { detail: { edgeId: i.edgeId } })),
        160,
      );
    } else if (i.sheetId) {
      setActiveSheet(i.sheetId);
      if (i.nodeId) {
        selectNode(i.nodeId);
        window.setTimeout(
          () => window.dispatchEvent(new CustomEvent('skema:focus', { detail: { nodeIds: [i.nodeId] } })),
          160,
        );
      }
    }
    onClose();
  };

  return (
    <div style={overlayStyle} onMouseDown={onClose}>
      <div style={panelStyle} onMouseDown={(e) => e.stopPropagation()}>
        <div style={titleBarStyle}>
          <span style={titleStyle}>Rule Checker</span>
          <button style={closeBtnStyle} onClick={onClose} title="Close">
            <X size={14} />
          </button>
        </div>

        {/* severity summary / filters */}
        <div style={filterRowStyle}>
          {(['error', 'warning', 'info'] as const).map((sev) => {
            const m = SEVERITY_META[sev];
            const off = hidden.has(sev);
            return (
              <button
                key={sev}
                onClick={() => toggle(sev)}
                title={off ? `Show ${m.label.toLowerCase()}` : `Hide ${m.label.toLowerCase()}`}
                style={{
                  ...chipStyle,
                  opacity: off ? 0.45 : 1,
                  borderColor: off ? 'var(--panel-border)' : m.color,
                }}
              >
                <m.Icon size={13} style={{ color: m.color }} />
                <span style={{ fontWeight: 800 }}>{counts[sev]}</span>
                <span style={{ color: 'var(--text-muted)' }}>{m.label}</span>
              </button>
            );
          })}
          <div style={{ flex: 1 }} />
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
            {issues.length} issue{issues.length === 1 ? '' : 's'} · {sheets.length} sheets
          </span>
        </div>

        {/* list */}
        <div style={listStyle}>
          {issues.length === 0 ? (
            <div style={emptyStyle}>
              <CheckCircle2 size={34} style={{ color: 'var(--selection)' }} strokeWidth={1.6} />
              <div style={{ fontWeight: 700, marginTop: 10 }}>No issues found</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                The project passes all design-rule checks.
              </div>
            </div>
          ) : visible.length === 0 ? (
            <div style={emptyStyle}>
              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                All issue types are hidden — re-enable a filter above.
              </div>
            </div>
          ) : (
            visible.map((i) => {
              const m = SEVERITY_META[i.severity];
              return (
                <button key={i.id} style={rowStyle} onClick={() => navigate(i)} title="Go to issue">
                  <m.Icon size={15} style={{ color: m.color, flexShrink: 0, marginTop: 1 }} />
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={rowMsgStyle}>{i.message}</span>
                    <span style={rowMetaStyle}>
                      <span style={ruleTagStyle}>{i.rule}</span>
                      {i.sheetName ? ` · ${i.sheetName}` : ''}
                    </span>
                  </span>
                </button>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

const overlayStyle: CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.45)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 9999,
};
const panelStyle: CSSProperties = {
  width: 560,
  maxWidth: '92vw',
  maxHeight: '80vh',
  display: 'flex',
  flexDirection: 'column',
  background: 'var(--panel)',
  color: 'var(--text)',
  border: '1px solid var(--panel-border-strong)',
  borderTop: '2px solid var(--accent)',
  borderRadius: 'var(--radius)',
  boxShadow: 'var(--shadow-lg)',
  overflow: 'hidden',
};
const titleBarStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '8px 10px 8px 14px',
  background: 'linear-gradient(var(--chrome-top), var(--chrome-bottom))',
  borderBottom: '1px solid var(--chrome-border)',
};
const titleStyle: CSSProperties = {
  fontSize: 11,
  fontWeight: 800,
  letterSpacing: 0.8,
  textTransform: 'uppercase',
  color: 'var(--text)',
};
const closeBtnStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 24,
  height: 22,
  border: '1px solid var(--panel-border)',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--panel)',
  color: 'var(--text-muted)',
  cursor: 'pointer',
  padding: 0,
};
const filterRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '8px 12px',
  borderBottom: '1px solid var(--panel-border)',
  background: 'var(--panel-alt)',
};
const chipStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  padding: '3px 9px',
  border: '1px solid var(--panel-border)',
  borderRadius: 999,
  background: 'var(--panel)',
  color: 'var(--text)',
  fontSize: 11,
  cursor: 'pointer',
};
const listStyle: CSSProperties = {
  overflowY: 'auto',
  padding: 6,
  minHeight: 120,
};
const emptyStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '40px 16px',
  textAlign: 'center',
};
const rowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 9,
  width: '100%',
  textAlign: 'left',
  padding: '7px 9px',
  border: '1px solid transparent',
  borderRadius: 'var(--radius-sm)',
  background: 'transparent',
  color: 'var(--text)',
  cursor: 'pointer',
};
const rowMsgStyle: CSSProperties = {
  display: 'block',
  fontSize: 12.5,
  lineHeight: 1.35,
};
const rowMetaStyle: CSSProperties = {
  display: 'block',
  fontSize: 10.5,
  color: 'var(--text-muted)',
  marginTop: 2,
};
const ruleTagStyle: CSSProperties = {
  fontFamily: 'ui-monospace, Consolas, monospace',
  color: 'var(--text-dim)',
};
